import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { PLANNING_MIN_WIDTH_SIDE, SavedToLocalstorage } from '@app/shared/constants';
import { replaceAccents } from '@app/shared/utils';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { EventApi } from '@fullcalendar/core';
import { Draggable } from '@fullcalendar/interaction';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select } from '@ngxs/store';
import { SlotKind } from '@radioking/planning';
import { ProgramsState } from '@radioking/programs';
import { SchedulerCoreState } from '@radioking/scheduler-core';
import {
  CloseCurrentPanel,
  DynamicEnabledPanel,
  ToogleSidenavOpacity,
} from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import {
  BehaviorSubject,
  combineLatest,
  fromEvent,
  Observable,
  Subscription,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
} from 'rxjs/operators';
import { AddSlotFromSidenav } from '../../states/planning.actions';

interface TypeTag {
  id: string;
  translationKey: string;
}

@Component({
  selector: 'rk-planning-slot-add',
  templateUrl: './planning-slot-add.component.html',
  styleUrls: ['./planning-slot-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningSlotAddComponent implements OnInit, OnDestroy, DynamicEnabledPanel {
  @Select(SchedulerCoreState.allNotEmptyPlaylistsForDropdown)
  playlistsUnfiltered$: Observable<SugestionInput[]>;

  @Select(ProgramsState.allNotEmptyProgramsForDropdown)
  programsUnfiltered$: Observable<SugestionInput[]>;

  enums = { SlotKind };

  playlists$: Observable<SugestionInput[]>;
  programs$: Observable<SugestionInput[]>;

  tagList: TypeTag[] = [
    {
      id: 'all',
      translationKey: marker('PLAYLIST.ADD_TRACKS.ALL'),
    },
  ];
  allTypes = 'all';

  searchSubject = new BehaviorSubject<string>('');

  selectedType = new BehaviorSubject<string>(this.allTypes);

  planningStorageKey = SavedToLocalstorage.PLANNING_SIDE_PANEL;

  subscription = new Subscription();

  @HostBinding('class.dragging')
  dragging = false;

  $playlistMaxHeight = signal(1000);

  constructor(private readonly actions$: Actions) {}

  hasToShowConfirmOnExit(): boolean {
    return false;
  }

  isDirty(): boolean {
    return false;
  }

  ngOnInit() {
    localStorage.setItem(this.planningStorageKey, '1');

    const draggableEl = document.getElementById('fc-external');

    new Draggable(draggableEl, {
      itemSelector: '.fc-event',
      eventData: eventEl => {
        if (window.innerWidth <= PLANNING_MIN_WIDTH_SIDE) {
          this.toogleSidenavOpacity();
        }
        const eventDuration = Number.parseFloat(
          eventEl.querySelector('.duration').getAttribute('data-duration'),
        );

        return {
          title: (eventEl.querySelector('.name') as HTMLElement).innerText,
          duration: Math.min(eventDuration || 60, 7200) * 1000,
          color: eventEl.getAttribute('data-color'),
        };
      },
    });

    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(ToogleSidenavOpacity))
        .pipe(filter(({ lessOpacity }) => lessOpacity))
        .subscribe(() => this.mouseUpEvent()),
    );

    this.subscription.add(
      this.playlistsUnfiltered$
        .pipe(
          filter(
            p =>
              p?.length > 0 &&
              this.tagList.findIndex(t => t.id === SlotKind.playlist) < 0,
          ),
        )
        .subscribe(p => {
          this.tagList = [
            ...this.tagList,
            {
              id: SlotKind.playlist,
              translationKey: 'SCHEDULE.PLANNING.EDIT_MODAL_TAB_PLAYLIST',
            },
          ];
        }),
    );
    this.subscription.add(
      this.programsUnfiltered$
        .pipe(
          filter(
            p =>
              p?.length > 0 && this.tagList.findIndex(t => t.id === SlotKind.program) < 0,
          ),
        )
        .subscribe(p => {
          this.tagList = [
            ...this.tagList,
            {
              id: SlotKind.program,
              translationKey: 'SCHEDULE.PLANNING.EDIT_MODAL_TAB_PROGRAM',
            },
          ];
        }),
    );

    this.programs$ = combineLatest([
      this.programsUnfiltered$,
      this.searchSubject
        .asObservable()
        .pipe(distinctUntilChanged(), debounceTime(200), startWith('')),
    ]).pipe(map(([programs, search]) => this.search(programs, search)));

    this.playlists$ = combineLatest([
      this.playlistsUnfiltered$,
      this.searchSubject
        .asObservable()
        .pipe(distinctUntilChanged(), debounceTime(200), startWith('')),
    ]).pipe(map(([playlists, search]) => this.search(playlists, search)));

    // Calcule playlist div height
    combineLatest([
      this.programs$.pipe(filter(p => !!p)),
      this.selectedType,
      fromEvent(window, 'resize').pipe(debounceTime(100), startWith(window.innerHeight)),
    ]).subscribe(([pr, type]) => {
      const listHeight = window.innerHeight - 161;
      const prHeight = pr.length * 40 + 30;
      if (type !== 'all') {
        this.$playlistMaxHeight.set(listHeight);
      } else if (prHeight < listHeight / 2) {
        this.$playlistMaxHeight.set(listHeight - prHeight);
      } else {
        this.$playlistMaxHeight.set(listHeight / 2 - 30);
      }
    });
  }

  addSlotClicked(event: SugestionInput, type: SlotKind) {
    const slot: Partial<EventApi> = {
      start: new Date(),
      end: new Date(),
      extendedProps: {},
    };
    slot.extendedProps[type === SlotKind.program ? 'program' : 'playlist'] = event.id;
    slot.extendedProps['kind'] = type;
    this.addSlotFromSidenav(slot);
  }

  search(data: SugestionInput[], search: string): SugestionInput[] {
    return data?.filter(p =>
      replaceAccents(p.name.toLowerCase()).includes(replaceAccents(search.toLowerCase())),
    );
  }

  mouseUpEvent() {
    fromEvent<MouseEvent>(document, 'mouseup')
      .pipe(take(1))
      .subscribe(() => this.toogleSidenavOpacity(false));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  @Dispatch()
  closePanel = () => {
    localStorage.removeItem(this.planningStorageKey);
    return new CloseCurrentPanel(true);
  };

  selectType(type: string) {
    this.selectedType.next(type);
  }

  onChangeSearch(str: string) {
    this.searchSubject.next(str);
  }

  getBtnHoverStyle(): object {
    return {
      width: '29px',
      height: '29px',
    };
  }

  @Dispatch()
  toogleSidenavOpacity = (less = true) => new ToogleSidenavOpacity(less);

  @Dispatch()
  addSlotFromSidenav = (slot: Partial<EventApi>) => new AddSlotFromSidenav(slot);
}
